import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Fab, Grid, type Theme, useMediaQuery } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { PermissionArea } from '@pbt/pbt-ui-components'

import DialogNames from '~/constants/DialogNames'
import { getCRUDByArea } from '~/store/reducers/auth'
import { addSearch } from '~/utils'
import useDialog from '~/utils/useDialog'

import DocumentDetails from './DocumentDetails'
import DocumentsTableComponent from './DocumentsTableComponent'

const useStyles = makeStyles(
  () => ({
    button: {
      minWidth: 120,
      height: 40,
    },
  }),
  { name: 'DocumentsPage' },
)

const DocumentsPage = () => {
  const navigate = useNavigate()
  const { documentId } = useParams()
  const location = useLocation()
  const isCreate = location.pathname.endsWith('create')
  const classes = useStyles()
  const permissions = useSelector(getCRUDByArea(PermissionArea.FORM))
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'))
  const { t } = useTranslation('Common')

  const [openNewDocumentDialog] = useDialog(DialogNames.DOCUMENT)

  const handleOpenNewDocumentDialog = () => {
    openNewDocumentDialog({
      PreviewProps: {
        hideEmail: true,
        hidePrint: true,
      },
    })
  }

  useEffect(() => {
    if (isCreate) {
      handleOpenNewDocumentDialog()
    }
  }, [isCreate])

  const onDetailsClose = () => {
    navigate(addSearch(location, '/admin/catalog/documents'))
  }

  if (isMobile && typeof documentId !== 'undefined') {
    return <DocumentDetails itemId={documentId} onClose={onDetailsClose} />
  }

  const headerButtons = (
    <Grid container item justifyContent="flex-end" px={3} py={2}>
      <Fab
        className={classes.button}
        color="inherit"
        variant="extended"
        onClick={handleOpenNewDocumentDialog}
      >
        {t('Common:ADD_NEW')}
      </Fab>
    </Grid>
  )

  return (
    <Grid container item flex={1} wrap="nowrap">
      <DocumentsTableComponent
        documentId={documentId}
        headerButtons={permissions.create && headerButtons}
        onDetailsClose={onDetailsClose}
      />
    </Grid>
  )
}

export default DocumentsPage
